import React from 'react';
import { Link } from '@tanstack/react-router';
import {
  Banner,
  Button,
  Flex,
  Select,
  TagBadge,
  Text,
  TextArea,
  TextField,
  type SelectOption,
} from '@chatsdet/ui';
import { MagicWand, Plus, Trash, Warning } from '@phosphor-icons/react';
import { formatRunnerEnvVar, parseRunnerEnvSource } from '@chatsdet/types';
import {
  autoMapUnmappedRows,
  classifyEnvMappingRow,
  countAutoMappable,
  parseEnvKeyPaste,
  suggestSourceForKey,
  type EnvMappingContext,
  type EnvMappingRow,
  type EnvMappingRowStatus,
} from '../../../../features/test-suites/envMappingStatus';

/** The three things a `.env` key can read from, as the row's first dropdown. */
const SOURCE_KIND_OPTIONS: SelectOption[] = [
  { id: 'ENV', label: 'Environment variable', description: 'From the target environment' },
  { id: 'USER', label: 'Persona variable', description: 'From the bound login persona' },
  { id: 'literal', label: 'Literal value', description: 'A plain string, stored as typed' },
];

type SourceKind = 'ENV' | 'USER' | 'literal';

/** Which dropdown state a persisted source string corresponds to. */
function sourceKindOf(source: string): SourceKind {
  const parsed = parseRunnerEnvSource(source);
  return parsed.kind === 'var' ? parsed.scope : 'literal';
}

/**
 * The status chip for one row.
 *
 * Blue for "this will resolve", orange for "this will not" — never red, which
 * is reserved for destructive actions. The two failure chips deep-link to the
 * screen that fixes them, because the alternative is a customer reading
 * "no value" and having no idea where the value would come from.
 */
function RowStatus({
  status,
  propertyId,
}: {
  status: EnvMappingRowStatus;
  propertyId: string | null;
}) {
  switch (status.kind) {
    case 'unmapped':
      return (
        <TagBadge
          name="Unmapped"
          color="gray"
          size="sm"
          hideDot
          tooltip="Not written into the container until it has a source"
        />
      );
    case 'literal':
      return (
        <TagBadge
          name="Literal"
          color="gray"
          size="sm"
          hideDot
          tooltip="Stored as typed and written into the .env verbatim"
        />
      );
    case 'resolvable':
      return (
        <TagBadge
          name={status.sensitive ? 'Mapped · sensitive' : 'Mapped'}
          color="blue"
          size="sm"
          hideDot
          tooltip={
            status.sensitive
              ? 'Resolves to a secret value, decrypted only inside the container'
              : 'Resolves to a value at run time'
          }
        />
      );
    case 'no-environment':
      return (
        <TagBadge
          name="No target environment"
          color="orange"
          size="sm"
          hideDot
          tooltip="Pick a target environment above — there is nothing for this to resolve against"
        />
      );
    case 'no-value':
      return propertyId ? (
        <Link
          to="/applications/$propertyId/environments"
          params={{ propertyId }}
          className="shrink-0 hover:opacity-80"
        >
          <TagBadge
            name="No value in this environment"
            color="orange"
            size="sm"
            hideDot
            tooltip={`"${status.name}" is declared but has no value in the selected environment — open the Environments tab to fill it in`}
          />
        </Link>
      ) : (
        <TagBadge name="No value in this environment" color="orange" size="sm" hideDot />
      );
    case 'unknown-name':
      return (
        <TagBadge
          name="Unknown variable"
          color="orange"
          size="sm"
          hideDot
          tooltip={`Nothing declares "${status.name}" in the ${
            status.scope === 'USER' ? 'persona' : 'application'
          } — it was probably renamed or removed`}
        />
      );
  }
}

export interface EnvMappingEditorProps {
  rows: EnvMappingRow[];
  onRowsChange: (rows: EnvMappingRow[]) => void;
  context: EnvMappingContext;
  /** Owning property — powers the deep links out of the failure chips. */
  propertyId: string | null;
  /** Name of the selected target environment, for the "no value" copy. */
  environmentName: string | null;
  /** True when no persona is bound, so `$var.USER.*` cannot resolve at all. */
  hasPersona: boolean;
}

/**
 * The `.env` mapping editor — the one surface where a customer says what their
 * suite is allowed to see.
 *
 * @remarks The mapping is not just a convenience map: it is the secret-injection
 * ALLOWLIST. Only keys listed here are decrypted and written into the runner's
 * `.env`, so the editor's job is as much to make the blast radius legible as it
 * is to make mapping fast.
 *
 * The bulk affordances are load-bearing rather than nice-to-have. A real suite
 * arrives with a dozen keys, most of them mechanically named after variables the
 * customer has already declared on the property; mapping those one dropdown at a
 * time is where onboarding stalls. Paste the keys, press one button, and what is
 * left is exactly the set that needs a human decision.
 */
export function EnvMappingEditor({
  rows,
  onRowsChange,
  context,
  propertyId,
  environmentName,
  hasPersona,
}: EnvMappingEditorProps) {
  const [pasteOpen, setPasteOpen] = React.useState(false);
  const [pasteText, setPasteText] = React.useState('');

  const envOptions = React.useMemo<SelectOption[]>(
    () =>
      context.envDefinitions.map((d) => ({
        id: d.name,
        label: d.name,
        description: d.description,
      })),
    [context.envDefinitions]
  );
  const userOptions = React.useMemo<SelectOption[]>(
    () =>
      context.userDefinitions.map((d) => ({
        id: d.name,
        label: d.name,
        description: d.description,
      })),
    [context.userDefinitions]
  );

  const autoMappable = countAutoMappable(rows, context);
  const unmappedCount = rows.filter((r) => !r.source.trim()).length;
  const sensitiveCount = rows.filter(
    (r) => classifyEnvMappingRow(r, context).kind === 'resolvable'
  ).length;

  const updateRow = (id: string, patch: Partial<EnvMappingRow>) =>
    onRowsChange(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const addRow = () =>
    onRowsChange([...rows, { id: `new:${crypto.randomUUID()}`, key: '', source: '' }]);

  const removeRow = (id: string) => onRowsChange(rows.filter((r) => r.id !== id));

  const applyAutoMap = () => onRowsChange(autoMapUnmappedRows(rows, context).rows);

  const applyPaste = () => {
    const existing = new Set(rows.map((r) => r.key.trim()));
    const fresh = parseEnvKeyPaste(pasteText).filter((k) => !existing.has(k));
    if (fresh.length > 0) {
      onRowsChange([
        ...rows,
        // Pre-fill the obvious ones on the way in. Someone who pastes their
        // `.env.example` and immediately sees most of it mapped understands the
        // model in one step; someone who sees twenty-five empty dropdowns does
        // not get that far.
        ...fresh.map((key) => ({
          id: `new:${crypto.randomUUID()}`,
          key,
          source: suggestSourceForKey(key, context) ?? '',
        })),
      ]);
    }
    setPasteText('');
    setPasteOpen(false);
  };

  /** Change a row's source kind, keeping the name where the namespaces overlap. */
  const changeKind = (row: EnvMappingRow, kind: SourceKind) => {
    const parsed = parseRunnerEnvSource(row.source);
    if (kind === 'literal') {
      updateRow(row.id, { source: parsed.kind === 'literal' ? parsed.value : '' });
      return;
    }
    const name = parsed.kind === 'var' ? parsed.name : '';
    updateRow(row.id, { source: name ? formatRunnerEnvVar(kind, name) : '' });
  };

  return (
    <Flex direction="column" gap="md">
      <Banner variant="warning">
        <Text size="sm">
          <strong>These values are written into a <code>.env</code> inside a container
          running your own test code.</strong>{' '}
          Anything you map here is readable by that code and by anything it imports, so map a
          least-privilege test persona — never a production account or an admin credential you
          would not hand to a third-party dependency.
        </Text>
      </Banner>

      <Flex align="center" gap="sm" wrap>
        <Text size="xs" className="text-muted-foreground">
          {rows.length} key{rows.length === 1 ? '' : 's'}
          {sensitiveCount > 0 ? ` · ${sensitiveCount} will resolve` : ''}
          {unmappedCount > 0 ? ` · ${unmappedCount} unmapped` : ''}
          {environmentName ? ` · resolving against ${environmentName}` : ''}
        </Text>
        <div className="ml-auto flex items-center gap-2">
          {autoMappable > 0 && (
            <Button
              size="sm"
              variant="secondary"
              iconLeft={<MagicWand size={14} />}
              onPress={applyAutoMap}
            >
              Map {autoMappable} by name
            </Button>
          )}
          <Button
            size="sm"
            variant="secondary"
            onPress={() => setPasteOpen((open) => !open)}
          >
            {pasteOpen ? 'Cancel' : 'Paste keys'}
          </Button>
          <Button size="sm" variant="secondary" iconLeft={<Plus size={14} />} onPress={addRow}>
            Add key
          </Button>
        </div>
      </Flex>

      {pasteOpen && (
        <div className="rounded-md border border-border bg-muted/30 p-3">
          <TextArea
            label="Paste your .env keys"
            description="One per line. Only the key names are read — any =value is ignored on purpose, so pasting a real .env never sends its secrets here."
            placeholder={'BASE_URL=\nADMIN_USER=\n# comments are skipped'}
            value={pasteText}
            onChange={setPasteText}
            rows={6}
          />
          <Flex justify="end" gap="sm" className="mt-2">
            <Button size="sm" variant="secondary" onPress={() => setPasteOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="primary"
              isDisabled={parseEnvKeyPaste(pasteText).length === 0}
              onPress={applyPaste}
            >
              Add {parseEnvKeyPaste(pasteText).length} key
              {parseEnvKeyPaste(pasteText).length === 1 ? '' : 's'}
            </Button>
          </Flex>
        </div>
      )}

      {!hasPersona && context.userDefinitions.length === 0 && (
        <Flex align="center" gap="sm">
          <Warning size={14} className="shrink-0 text-warning" />
          <Text size="xs" className="text-muted-foreground">
            No login persona is bound, so <code>$var.USER.*</code> has nothing to resolve
            against. Pick one under Target above to map persona variables.
          </Text>
        </Flex>
      )}

      {rows.length === 0 ? (
        <div className="rounded-md border border-dashed border-border p-6 text-center">
          <Text size="sm" weight="medium">
            No environment variables mapped
          </Text>
          <Text size="xs" className="mt-1 text-muted-foreground">
            Your suite starts with an empty <code>.env</code>, and tests that read no variables run
            as-is. Paste the keys your tests read and map each one to a value.
          </Text>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {rows.map((row) => {
            const kind = sourceKindOf(row.source);
            const parsed = parseRunnerEnvSource(row.source);
            const status = classifyEnvMappingRow(row, context);
            return (
              <div
                key={row.id}
                className="grid grid-cols-1 items-start gap-2 rounded-md border border-border bg-muted/20 p-2.5 md:grid-cols-[minmax(0,1fr)_minmax(0,10rem)_minmax(0,1fr)_auto_auto]"
              >
                <TextField
                  aria-label=".env key"
                  placeholder="MY_TEST_VAR"
                  value={row.key}
                  onChange={(key) => updateRow(row.id, { key })}
                />
                <Select
                  aria-label="Source kind"
                  items={SOURCE_KIND_OPTIONS}
                  selectedKey={kind}
                  onSelectionChange={(key) => changeKind(row, String(key) as SourceKind)}
                />
                {kind === 'literal' ? (
                  <TextField
                    aria-label="Literal value"
                    placeholder="a plain value"
                    value={parsed.kind === 'literal' ? parsed.value : ''}
                    onChange={(value) => updateRow(row.id, { source: value })}
                  />
                ) : (
                  <Select
                    aria-label="Variable"
                    placeholder={kind === 'ENV' ? 'Choose an app variable' : 'Choose a persona variable'}
                    items={kind === 'ENV' ? envOptions : userOptions}
                    selectedKey={parsed.kind === 'var' ? parsed.name : null}
                    onSelectionChange={(name) =>
                      updateRow(row.id, { source: formatRunnerEnvVar(kind, String(name)) })
                    }
                    emptyText={
                      kind === 'ENV'
                        ? 'This application declares no environment variables yet'
                        : 'This persona declares no variables yet'
                    }
                  />
                )}
                <div className="flex items-center pt-1.5">
                  <RowStatus status={status} propertyId={propertyId} />
                </div>
                <Button
                  size="sm"
                  variant="icon"
                  iconOnly
                  tooltip="Remove key"
                  aria-label={`Remove ${row.key || 'this key'}`}
                  onPress={() => removeRow(row.id)}
                >
                  <Trash size={14} />
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </Flex>
  );
}
